import { z } from 'zod';

import { PERSONAS_HUMANAS } from '../authz/personas';
import { apenasDigitos, isValidCpf } from '../shared/documento';
import { instante, listOutput, page } from './common';
import { defineEndpoint } from './endpoint';

export const Persona = z.enum(PERSONAS_HUMANAS);
const StatusUsuario = z.enum(['pendente', 'ativo', 'desativado']);
const IdParam = z.object({ id: z.uuid() });

/** Ativo sem usar o sistema há estes dias entra no atalho "Sem usar há 30 dias". */
export const SEM_ACESSO_DIAS = 30;
/** Intervalo mínimo entre dois envios do convite (`resend_invite`, baseline.sql:11716). */
export const COOLDOWN_CONVITE_MIN = 10;

/** Minutos que faltam para reenviar o convite (arredondado para cima); `null` = liberado. Mesma conta na API e na tela. */
export function minutosParaReenviar(ultimoEnvio: Date | null, agora: Date): number | null {
  if (!ultimoEnvio) return null;
  const faltam = ultimoEnvio.getTime() + COOLDOWN_CONVITE_MIN * 60_000 - agora.getTime();
  return faltam > 0 ? Math.ceil(faltam / 60_000) : null;
}
/** Dias do gráfico de uso da página da pessoa (hoje incluso). */
export const USO_JANELA_DIAS = 30;
const email = z.string().trim().toLowerCase().pipe(z.email());

/** Linha de `list_users` (baseline.sql:8577-8590), mesmos nomes de campo. */
export const Usuario = z.object({
  id: z.uuid(),
  email: z.string(),
  nome: z.string().nullable(),
  cpf: z.string().nullable(),
  telefone: z.string().nullable(),
  status: z.string().nullable(),
  partner_id: z.uuid().nullable(),
  partner_nome: z.string().nullable(),
  role_id: z.uuid().nullable(),
  /** Só para exibição; a UI decide por `persona`. */
  role_name: z.string().nullable(),
  persona: Persona.nullable(),
  created_at: instante.nullable(),
  last_invite_sent_at: instante.nullable(),
  /** Último uso do sistema (`usuario_acessos`); `null` = sem uso desde o início do registro. */
  ultimo_acesso_em: instante.nullable(),
  aparelho: z.string().nullable(),
  contratos_ativos: z.number().int(),
  propostas_abertas: z.number().int(),
  /** Parceiros na carteira dela agora (executivo de contas): saem ao desativar ou trocar o papel. */
  parceiros_na_carteira: z.number().int(),
  /** Ativo sem usar o sistema há `SEM_ACESSO_DIAS` (mesma regra do atalho). */
  sem_uso_recente: z.boolean(),
  /** O que o ator pode fazer com a pessoa (`domain/identidade/acoes.ts`): a tela só mostra o permitido. */
  pode_gerir: z.boolean(),
  pode_acessar_como: z.boolean(),
  pode_editar_dados: z.boolean(),
  pode_trocar_papel: z.boolean(),
});
export type Usuario = z.infer<typeof Usuario>;

export const Papel = z.object({ id: z.uuid(), name: z.string(), persona: Persona });
export type Papel = z.infer<typeof Papel>;

const ORDENS_USUARIOS = ['nome', 'acesso', 'contratos'] as const;

export const FiltroUsuarios = z.object({
  ...page,
  parceiroId: z.uuid().optional(),
  status: StatusUsuario.optional(),
  persona: Persona.optional(),
  /** Ativo sem usar o sistema há `SEM_ACESSO_DIAS` (atalho da lista). */
  fila: z.enum(['sem_acesso']).optional(),
  /** Sem ordem: mais recentes primeiro (paridade com `list_users`). `acesso` = há mais tempo sem usar primeiro. */
  ordem: z.enum(ORDENS_USUARIOS).optional(),
  /** Vazio ≡ sem busca (no legado, `ILIKE '%%'` casa todos). */
  busca: z.string().trim().transform((v) => v || undefined).optional(),
});
export type FiltroUsuarios = z.infer<typeof FiltroUsuarios>;

/** Substitui `list_users`. `parceiroId` só vale para `super_admin`; `admin_parceiro` tem o próprio forçado. */
export const listarUsuarios = defineEndpoint({
  method: 'GET',
  path: '/api/usuarios',
  query: FiltroUsuarios,
  output: listOutput(Usuario),
});

/** Substitui `list_roles`: só os papéis que o ator pode atribuir. */
export const listarPapeis = defineEndpoint({
  method: 'GET',
  path: '/api/usuarios/papeis',
  query: z.object({ ...page }),
  output: listOutput(Papel),
});

/** Senha do próprio ator, conferida na rota: exigida quando o papel envolvido é Super-Administrador. */
const senhaDoAtor = z.string().max(200).optional();

export const alterarPapel = defineEndpoint({
  method: 'PATCH',
  path: '/api/usuarios/:id/papel',
  params: IdParam,
  body: z.strictObject({ role_id: z.uuid(), senha: senhaDoAtor }),
  output: z.object({ id: z.uuid(), role_id: z.uuid() }),
});

/** Contagens dos atalhos da lista de usuários, no escopo do ator. */
export const AtalhosUsuarios = z.object({
  convitesSemResposta: z.object({ total: z.number().int(), maisAntigoEm: instante.nullable() }),
  semAcesso: z.object({ total: z.number().int(), maisAntigoEm: instante.nullable() }),
});
export type AtalhosUsuarios = z.infer<typeof AtalhosUsuarios>;

export const atalhosUsuarios = defineEndpoint({
  method: 'GET',
  path: '/api/usuarios/atalhos',
  output: AtalhosUsuarios,
});

const Evento = z.object({
  tipo: z.enum(['convidado', 'convite_reenviado', 'convite_corrigido', 'papel_alterado', 'desativado', 'reativado', 'dados_alterados']),
  /** Forma por tipo em `domain/identidade/eventos.ts` (`EventoDeUsuario`). */
  detalhe: z.record(z.string(), z.unknown()).nullable(),
  em: instante,
  autor_nome: z.string().nullable(),
});

export const UsuarioDetalhe = Usuario.extend({
  sessoes: z.array(z.object({ id: z.uuid(), aparelho: z.string().nullable(), ultimo_uso_em: instante })),
  /** Um ponto por dia de `USO_JANELA_DIAS` (dia em São Paulo, mais antigo primeiro), com zero nos dias sem uso. */
  uso_diario: z.array(z.object({ dia: z.iso.date(), acessos: z.number().int() })),
  /** Contratos emitidos pela pessoa no mês corrente em São Paulo. */
  contratos_no_mes: z.number().int(),
  eventos: z.array(Evento),
});
export type UsuarioDetalhe = z.infer<typeof UsuarioDetalhe>;

export const lerUsuario = defineEndpoint({
  method: 'GET',
  path: '/api/usuarios/:id',
  params: IdParam,
  output: UsuarioDetalhe,
});

const telefone = z
  .string()
  .transform(apenasDigitos)
  .pipe(z.string().min(10).max(13));

export const editarUsuario = defineEndpoint({
  method: 'PATCH',
  path: '/api/usuarios/:id',
  params: IdParam,
  body: z.strictObject({ nome: z.string().trim().min(1).max(200), telefone }),
  output: z.object({ id: z.uuid() }),
});

/** Convite pendente com e-mail errado: troca o e-mail e reenvia o convite. */
export const corrigirEmailDoConvite = defineEndpoint({
  method: 'POST',
  path: '/api/usuarios/:id/convite/email',
  params: IdParam,
  body: z.strictObject({ email }),
  output: z.object({ id: z.uuid(), email: z.string() }),
});

export const desconectarAparelho = defineEndpoint({
  method: 'DELETE',
  path: '/api/usuarios/:id/sessoes/:sessaoId',
  params: z.object({ id: z.uuid(), sessaoId: z.uuid() }),
  output: z.object({ id: z.uuid() }),
});

export const desconectarTodosOsAparelhos = defineEndpoint({
  method: 'DELETE',
  path: '/api/usuarios/:id/sessoes',
  params: IdParam,
  output: z.object({ id: z.uuid() }),
});

export const enviarLinkDeSenha = defineEndpoint({
  method: 'POST',
  path: '/api/usuarios/:id/link-de-senha',
  params: IdParam,
  output: z.object({ id: z.uuid() }),
});

const StatusAlterado = z.object({ id: z.uuid(), status: StatusUsuario });

export const desativarUsuario = defineEndpoint({
  method: 'POST',
  path: '/api/usuarios/:id/desativar',
  params: IdParam,
  output: StatusAlterado,
});

export const reativarUsuario = defineEndpoint({
  method: 'POST',
  path: '/api/usuarios/:id/reativar',
  params: IdParam,
  output: StatusAlterado,
});

/**
 * Rota Next existente (L1): mesmo path e corpo de resposta. `nomeConvidador` é aceito e ignorado
 * (vem do Actor, T02 §3.6.9). `role_id` passa a ser obrigatório (M5).
 */
export const convidarUsuario = defineEndpoint({
  method: 'POST',
  path: '/api/invite-user',
  body: z.strictObject({
    email,
    role_id: z.uuid(),
    partner_id: z.uuid().nullish(),
    nomeConvidador: z.string().max(120).optional(),
    senha: senhaDoAtor,
  }),
  output: z.object({ success: z.literal(true), message: z.string(), user_id: z.uuid() }),
});

/** Rota Next existente (L1). `nome_convidador` é aceito e ignorado (vem do Actor). */
export const reenviarConvite = defineEndpoint({
  method: 'POST',
  path: '/api/resend-invite',
  body: z.strictObject({ user_id: z.uuid(), nome_convidador: z.string().max(120).optional() }),
  output: z.object({ success: z.literal(true), message: z.string() }),
});

/** Contadores do sidebar (substituem os de `get_current_user_info` + `fn_get_renewal_badge_count` + chargebacks). */
export const Badges = z.object({
  solicitacoesPendentes: z.number().int(),
  sinistrosPendentes: z.number().int(),
  renovacoesPendentes: z.number().int(),
  chargebacksAbertos: z.number().int(),
});
export type Badges = z.infer<typeof Badges>;

export const lerBadges = defineEndpoint({ method: 'GET', path: '/api/me/badges', output: Badges });

/** Perfil do primeiro acesso (T02 §3.6.3): CPF validado no servidor (antes só no cliente). Só dígitos ficam. */
/** Mínimo da senha nova (convite, boas-vindas, redefinição): a API valida e a tela avisa com o mesmo número. */
export const SENHA_MIN_CARACTERES = 6;

export const DadosCadastro = z.strictObject({
  nome: z.string().trim().min(1).max(200),
  cpf: z
    .string()
    .transform(apenasDigitos)
    .refine(isValidCpf, { message: 'CPF inválido' }),
  telefone,
});
export type DadosCadastro = z.infer<typeof DadosCadastro>;
